import { EventProductMappedToTier } from 'legacy/common/types/models';
import MemberOnlyTierLabel from '../../../../../../../MemberOnlyTierLabel';
import ProductStatusChip from 'components/checkout/ProductStatusChip';
import { useCheckout } from 'features/events/checkout/context';
import { currencyFormat } from 'legacy/common/utils/currency';
import ToggleTierButton from './components/ToggleTierButton';
import QuantityButtons from '../../../../../../../QuantityButtons';
import { QuantityChangeEvents } from 'features/events/checkout/types';
import ProductCard from 'components/checkout/ProductCard';
import { useTranslation } from 'react-i18next';

type Props = {
  tier: EventProductMappedToTier;
};

const TierCard = ({ tier }: Props) => {
  const { t } = useTranslation(['checkout']);
  const {
    appliedDiscount,
    tickets,
    updateTicketTierQuantityAndSave,
    toggleTicketTierAndSave,
    checkoutSession,
    ticketTiers
  } = useCheckout();
  const isMemberOnlyAccess =
    tier?.requiredAssociationIds?.length > 0 ||
    tier?.requiredOrganizationIds?.length > 0;
  const tierPendingApproval =
    tier.requiresApproval &&
    (tier.requestStatus === 'pending' || !tier.requestStatus);

  /**
   * Determines if a ticket tier is selected based on various conditions:
   *  - Ticket is selected and no tier requiring approval has been selected
   *  - Ticket requires approval and has been selected
   */
  const isTicketTierSelected = Boolean(
    (tickets.selectedTicketTiers[tier._id] && !tickets.tierRequiringApproval) ||
      (tickets.tierRequiringApproval &&
        tickets.tierRequiringApproval === tier._id)
  );

  /**
   * Checks if the current tier is restricted by the applied discount code.
   * Returns true if there's a discount code applied with tierIds restrictions
   * and this tier is not in the allowed list.
   * Note: Only applies to main ticket tiers (admission), not addon tiers.
   */
  const isTierRestrictedByDiscountCode = Boolean(
    tier.type === 'admission' && // Only restrict main ticket tiers, not addons
      appliedDiscount.data?.tierIds &&
      appliedDiscount.data.tierIds.length > 0 &&
      !appliedDiscount.data.tierIds.includes(tier._id)
  );

  /**
   * Determines whether the tier is disabled based on various conditions:
   *  - Checkout session is currently loading
   *  - Discount data is currently loading
   *  - Ticket tier is considered disabled
   *  - A ticket tier requiring approval has been selected, and it's not this one.
   *  - Number of total tickets selected by the guest is >= to the max number of tickets allowed per transaction
   *    AND the ticket tier has not yet been selected by the guest
   *    (this check allows us to disable further tiers from being added but lets the guest decrease the number of tickets for an already selected tier).
   *  - Tier is restricted by the applied discount code's tierIds
   */
  const isTierDisabled =
    checkoutSession.isCheckoutDisabled ||
    checkoutSession.isLoadingCheckout ||
    appliedDiscount.isLoading ||
    ticketTiers.isTierDisabled(tier._id) ||
    isTierRestrictedByDiscountCode ||
    (tickets.tierRequiringApproval &&
      tickets.tierRequiringApproval !== tier._id) ||
    (tickets.totalAmountOfTicketsSelected >= tickets.maxTicketsPerTransaction &&
      !tickets.selectedTicketTiers[tier._id]);

  /**
   * Function called when a ticket tier is clicked.
   * It checks if the tier is disabled, if a ticket tier is already selected,
   * or if the maximum number of tickets per transaction has been reached.
   * If none of the conditions are met, it toggles the ticket tier and saves the ticket tier ID.
   */
  const onTierClick = () => {
    if (
      isTierDisabled ||
      isTicketTierSelected ||
      tickets.totalAmountOfTicketsSelected >= tickets.maxTicketsPerTransaction
    ) {
      return;
    }

    toggleTicketTierAndSave(tier._id);
  };

  const onQuantityChange = (event: QuantityChangeEvents) => {
    updateTicketTierQuantityAndSave(tier._id, event);
  };

  const onToggleApprovalTier = () => {
    tickets.toggleTierRequiringApproval(tier._id);
  };

  return (
    <ProductCard
      isSelected={isTicketTierSelected}
      isDisabled={isTierDisabled}
      onClick={tierPendingApproval ? onToggleApprovalTier : onTierClick}
      imageUrl={tier.imageUrl}
      isPrivate={tier.private}
      name={tier.name}
      description={tier.description}
      price={tier?.price}
      discountData={appliedDiscount.data}
      valueProp={
        tier?.savings
          ? t('checkout:saveWithBundle', {
              amount: currencyFormat(tier?.savings)
            })
          : undefined
      }
      AdditionalProductInfo={
        <>
          {isMemberOnlyAccess && <MemberOnlyTierLabel />}
          {isTierRestrictedByDiscountCode && (
            <div className="flex items-center gap-1 text-sm font-semibold rounded-lg py-1 px-2 bg-[#f0dddc] text-[#60150f]">
              <span className="flex-1">
                {t('checkout:restrictedByDiscountCode')}
              </span>
            </div>
          )}
          <ProductStatusChip
            startDate={tier.startDate}
            endDate={tier.endDate}
            isDisabled={tier.isDisabled}
            remaining={tier.remaining}
            requiresApproval={tier.requiresApproval}
            requestStatus={tier.requestStatus}
            questions={tier.questions}
          />
        </>
      }
      Actions={
        <>
          {tierPendingApproval ? (
            <ToggleTierButton
              isSelected={Boolean(tickets.tierRequiringApproval)}
            />
          ) : (
            <QuantityButtons
              totalAmountOfTicketsSelected={
                tickets.totalAmountOfTicketsSelected
              }
              maxTicketsPerTransaction={tickets.maxTicketsPerTransaction}
              remainingTicketsForTier={tier.remaining}
              selectedTicketTierQuantity={
                tickets.selectedTicketTiers[tier._id]?.quantity || 0
              }
              onQuantityChange={onQuantityChange}
            />
          )}
        </>
      }
    />
  );
};

export default TierCard;
